import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Typography,
} from '@mui/material';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import { ProgramSessionForDate } from '../../services/api/ProgramApiService';
import { useLanguage } from '../../i18n/LanguageContext';

interface ProgramSessionCardProps {
  session: ProgramSessionForDate;
}

function formatReps(repsCompleted: number[], setsCompleted: number): string {
  if (repsCompleted.length === 0) return `${setsCompleted}×?`;
  const allSame = repsCompleted.every((r) => r === repsCompleted[0]);
  if (allSame) return `${setsCompleted}×${repsCompleted[0]}`;
  return repsCompleted.join(', ');
}

export const ProgramSessionCard: React.FC<ProgramSessionCardProps> = ({ session }) => {
  const { t } = useLanguage();

  return (
    <Card variant="outlined" sx={{ mb: 1 }}>
      <CardContent sx={{ pb: '12px !important' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <EmojiEventsIcon sx={{ color: 'primary.main', fontSize: 18 }} />
          <Typography variant="subtitle2" fontWeight={600}>
            {session.program_name} · {session.template_name}
          </Typography>
        </Box>

        <Divider sx={{ mb: 1 }} />

        {/* Exercise rows */}
        {session.results.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t('programs.noDetailRecorded')}
          </Typography>
        ) : (
          session.results.map((result, i) => (
            <Box
              key={i}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                py: 0.5,
              }}
            >
              <Typography variant="body2" sx={{ flex: 1 }}>
                {result.exercise_name}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mx: 1 }}>
                {formatReps(result.reps_completed, result.sets_completed)} @ {result.weight_used_kg} kg
              </Typography>
              <Chip
                label={result.succeeded ? '✓' : '✗'}
                size="small"
                sx={{
                  backgroundColor: result.succeeded ? 'success.light' : 'warning.light',
                  color: result.succeeded ? 'success.dark' : 'warning.dark',
                  fontWeight: 600,
                  minWidth: 32,
                }}
              />
            </Box>
          ))
        )}

        {/* Notes */}
        {session.notes && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {session.notes}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};
